/**
 * LPA Factory Settings API
 *
 * Read / upsert operations for `core.mod_lpa_factory_settings`.
 * Each factory has at most one settings row (UNIQUE on tenant_id + factory_id).
 * When no row exists yet the UI should treat it as "defaults" — the DB columns
 * already define sensible defaults so an upsert with only the changed fields
 * is sufficient.
 */

import { supabase } from '@/integrations/supabase/client';
import type { LpaFactorySettings, UpsertFactorySettingsInput } from '../types';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Fetch the factory settings row for a given factory.
 * Returns `null` when no row has been persisted yet (the caller should
 * fall back to column defaults defined in the migration).
 */
export async function fetchFactorySettings(
  tenantId: string,
  factoryId: string,
): Promise<LpaFactorySettings | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_factory_settings')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('factory_id', factoryId)
    .maybeSingle();

  if (error) throw error;
  return data as unknown as LpaFactorySettings | null;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Create or update factory settings for a factory (upsert).
 *
 * Uses Postgres `ON CONFLICT (tenant_id, factory_id) DO UPDATE` semantics
 * via the Supabase `.upsert()` method with `onConflict`.
 */
export async function upsertFactorySettings(
  input: UpsertFactorySettingsInput,
): Promise<LpaFactorySettings> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_factory_settings')
    .upsert(
      {
        tenant_id: input.tenant_id,
        factory_id: input.factory_id,
        ...(input.week_start_day !== undefined && { week_start_day: input.week_start_day }),
        ...(input.eob_hour !== undefined && { eob_hour: input.eob_hour }),
        ...(input.schedule_generation_lead_days !== undefined && {
          schedule_generation_lead_days: input.schedule_generation_lead_days,
        }),
        ...(input.reminder_hours_before !== undefined && {
          reminder_hours_before: input.reminder_hours_before,
        }),
        ...(input.escalation_hours_after !== undefined && {
          escalation_hours_after: input.escalation_hours_after,
        }),
      },
      { onConflict: 'tenant_id,factory_id' },
    )
    .select()
    .single();

  if (error) throw error;
  return data as unknown as LpaFactorySettings;
}
